import { computed, ref, Ref } from 'vue';
import { LoadingStatuses } from '@/models/loading.interface';
import { SearchUserDto } from '@studyportals/campaign-management-api-interface';
import userPresenter from '@/presenters/user-presenter';
import store from '@/store';

export default class SearchInputCode {

	public searchInput: Ref<HTMLInputElement | null> = ref(null);
	public searchTerm: Ref<string> = ref('');
	public searchResults = ref<SearchUserDto[]>([]);
	public showDropdown = ref<boolean>(false);
	public searchWrapper = ref<HTMLDivElement | null>(null);
	public showSpinner = ref<boolean>(false);
	private debounceDelay = 1000;

	private debounceTimeout: NodeJS.Timeout | number | null = null;
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private boundHandleOutsideClick: (event: MouseEvent) => void;

	public isLoading = computed((): boolean => {
		const loadingStatus = store.getters.loadingStatusRights();
		return loadingStatus === LoadingStatuses.ACTIVE;
	});

	public searchResultsAsString = computed((): string => {
		return this.searchResults.value.map((item) => item.id).join(',');
	});

	public rightsUsersAsString = computed((): string => {
		return this.rightsUsers.value.map((item) => item.id).join(',');
	});

	private rightsUsers = computed((): SearchUserDto[] => {
		return store.getters.rightsUsers();
	});

	constructor() {
		this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
	}

	public mounted(): void {
		document.addEventListener('click', this.boundHandleOutsideClick);
	}

	public unmounted(): void {
		document.removeEventListener('click', this.boundHandleOutsideClick);
	}

	public setSearchTerm(): void {
		if (!this.searchInput.value) {
			return;
		}

		this.searchTerm.value = this.searchInput.value.value;

		if (this.searchTerm.value.trim() === '') {
			this.searchResults.value = [];
			return;
		}

		this.debounceSearch();
	}

	public revealDropdown(): void {
		this.showDropdown.value = true;
	}

	public resetDropdown(): void {
		this.showDropdown.value = false;
		this.showSpinner.value = false;
		this.searchResults.value = [];
		this.searchTerm.value = '';
	}

	private debounceSearch(): void {
		if (this.debounceTimeout) {
			clearTimeout(this.debounceTimeout as NodeJS.Timeout);
		}

		this.showSpinner.value = true;
		this.debounceTimeout = setTimeout(() => void this.delayedFunction(), this.debounceDelay);
	}

	private async delayedFunction(): Promise<void> {
		if (this.searchTerm.value.trim() === '') {
			this.searchResults.value = [];
			this.showSpinner.value = false;
			return;
		}

		this.showDropdown.value = true;
		await this.setResults();
		this.showSpinner.value = false;
	}

	private handleOutsideClick(event: MouseEvent): void {
		if (!this.searchWrapper.value || this.searchWrapper.value.contains(event.target as Node)) {
			return;
		}

		this.showDropdown.value = false;
	}

	private async setResults(): Promise<void> {
		this.searchResults.value = await userPresenter.searchUsers(this.searchTerm.value);
	}
}
